import { useEffect, useRef } from 'react';

const stakeholders = [
  {
    icon: 'ri-user-heart-line',
    title: 'For Patients',
    color: 'primary',
    points: [
      'Early detection yields an average of 10.74 additional years of healthy life expectancy',
      'At-home monitoring and progression tracking',
      'Non-invasive testing with no physical discomfort',
      'Continuous monitoring possible at minimal cost',
    ],
  },
  {
    icon: 'ri-stethoscope-line',
    title: 'For Healthcare Providers',
    color: 'accent',
    points: [
      'Combat physician burnout with automated initial screening and triage',
      'Clinical Decision Support Tool integrating into clinical workflows',
      'Faster diagnosis from years to minutes',
      'Objective measurement replacing subjective assessments',
    ],
  },
  {
    icon: 'ri-shield-star-line',
    title: 'For Health Insurers',
    color: 'secondary',
    points: [
      'Improve risk stratification and avoid long-term costs',
      'Early depression detection reduces annual treatment costs from $4,246 to $2,763',
      'Better population health management',
      'Reduce unnecessary specialist referrals',
    ],
  },
  {
    icon: 'ri-capsule-line',
    title: 'For Pharma Companies',
    color: 'primary',
    points: [
      'Modernize drug development with objective digital endpoints',
      'Replace subjective self-reporting in clinical trials',
      'Faster trial completion with real-time biomarker monitoring',
      'Enhanced patient recruitment through remote screening',
    ],
  },
];

const diagnosticData = [
  { condition: 'Depression', delay: '5 years', patients: '21 million', affected: '10.5 million' },
  { condition: 'Bipolar Disorder', delay: '6.46 years', patients: '2.8 million', affected: '1.8 million' },
  { condition: 'Alzheimer\'s Disease', delay: '2-3 years', patients: '6.5 million', affected: '3.2 million' },
  { condition: 'Parkinson\'s Disease', delay: '3+ years', patients: '90,000 new/year', affected: '45,000+' },
];

export default function MarketImpactSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-background-50 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-accent-500 uppercase">
              Market Impact
            </span>
          </div>
          <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-4">
            Transforming Clinical Diagnostics
          </h2>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-3xl mx-auto">
            Resolving the fundamental delay and cost issues in today&apos;s healthcare diagnostic pathway.
          </p>
        </div>

        {/* Diagnostic delay table */}
        <div className="reveal opacity-0 mb-16">
          <div className="bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
            <h3 className="font-body text-lg font-semibold text-foreground-50 mb-6">
              Diagnostic Delay & Patient Impact
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/70">
                    <th className="text-left font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-3 px-4">
                      Condition
                    </th>
                    <th className="text-left font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-3 px-4">
                      Avg. Delay to Diagnosis
                    </th>
                    <th className="text-left font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-3 px-4">
                      Patients Affected
                    </th>
                    <th className="text-left font-body text-xs font-semibold tracking-wider text-foreground-500 uppercase py-3 px-4">
                      Patients Affected by Delay
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosticData.map((row, i) => (
                    <tr key={i} className="border-b border-background-200/40 hover:bg-background-50/50 transition-colors">
                      <td className="font-body text-sm font-medium text-foreground-50 py-4 px-4">
                        {row.condition}
                      </td>
                      <td className="font-body text-sm text-accent-500 py-4 px-4">
                        {row.delay}
                      </td>
                      <td className="font-body text-sm text-foreground-400 py-4 px-4">
                        {row.patients}
                      </td>
                      <td className="font-body text-sm text-foreground-400 py-4 px-4">
                        {row.affected}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-accent-500/10 border border-accent-500/20">
              <p className="font-body text-sm text-foreground-400">
                <span className="text-foreground-50 font-medium">Key stat:</span> Delayed diagnosis leads to $47 billion in excess healthcare costs annually in the U.S. alone. Rare diseases take an average of 4.7 to 7.6 years to diagnose.
              </p>
            </div>
          </div>
        </div>

        {/* Stakeholder cards */}
        <div className="reveal opacity-0 grid grid-cols-1 md:grid-cols-2 gap-6">
          {stakeholders.map((stakeholder, i) => {
            const borderColor = stakeholder.color === 'primary'
              ? 'border-primary-500/20 hover:border-primary-500/40'
              : stakeholder.color === 'accent'
              ? 'border-accent-500/20 hover:border-accent-500/40'
              : 'border-secondary-500/20 hover:border-secondary-500/40';

            const bgColor = stakeholder.color === 'primary'
              ? 'bg-primary-500/10'
              : stakeholder.color === 'accent'
              ? 'bg-accent-500/10'
              : 'bg-secondary-500/10';

            const iconColor = stakeholder.color === 'primary'
              ? 'text-primary-500'
              : stakeholder.color === 'accent'
              ? 'text-accent-500'
              : 'text-secondary-500';

            return (
              <div
                key={i}
                className={`p-6 md:p-8 rounded-2xl bg-background-100 border ${borderColor} transition-all duration-500 ease-silk`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${bgColor}`}>
                    <i className={`${stakeholder.icon} ${iconColor} text-lg`} />
                  </div>
                  <h3 className="font-body text-lg font-semibold text-foreground-50">
                    {stakeholder.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {stakeholder.points.map((point, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full ${iconColor.replace('text-', 'bg-')} mt-2 shrink-0`} />
                      <p className="font-body text-sm text-foreground-400 leading-relaxed">
                        {point}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}